// components/SubscriptionAlert.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Alert,
  AlertTitle,
  LinearProgress,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Snackbar,
  CircularProgress,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Payment as PaymentIcon,
  Close as CloseIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  CreditCard as CreditCardIcon,
  Security as SecurityIcon,
  LocalOffer as LocalOfferIcon,
  Discount as DiscountIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import { formatDistanceToNow, format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { loadStripe } from '@stripe/stripe-js';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const SubscriptionAlert = ({ language, restaurant, onRefresh }) => {
  const { user } = useAuth();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // Promo code states
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState(false);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [originalPrice, setOriginalPrice] = useState(0);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);

  // Subscription plans
  const subscriptionPlans = [
    { 
      id: '2', 
      name: '1 Month', 
      name_ar: 'شهر واحد',
      days: 30, 
      price: 29.99,
      priceMYR: 29.99,
      priceUSD: 30.00,
      features: ['Full Access', 'Support', 'Updates']
    },
    { 
      id: '3', 
      name: '6 Months', 
      name_ar: '6 أشهر',
      days: 180, 
      price: 149.99,
      priceMYR: 149.99,
      priceUSD: 153.00,
      features: ['Full Access', 'Priority Support', 'Updates', 'Save 15%'],
      popular: true
    },
    { 
      id: '4', 
      name: '1 Year', 
      name_ar: 'سنة واحدة',
      days: 365, 
      price: 249.99,
      priceMYR: 249.99,
      priceUSD: 252.00,
      features: ['Full Access', 'Priority Support', 'Updates', 'Save 30%']
    },
    { 
      id: '5', 
      name: '5 Years', 
      name_ar: '5 سنوات',
      days: 1825, 
      price: 999.99,
      priceMYR: 999.99,
      priceUSD: 900.00,
      features: ['Lifetime Access', 'Premium Support', 'Updates', 'Save 50%'],
      bestValue: true
    }
  ];

  // Get subscription status from restaurant data
  const subscription = restaurant?.subscription || {};
  const isActive = subscription.is_active || false;
  const remainingDays = subscription.remaining_days || 0;
  const status = subscription.status || 'inactive';
  const expiryDate = subscription.expiry_date ? new Date(subscription.expiry_date) : null;
  const subscriptionType = subscription.subscription_type || null;

  // Determine alert severity and message
  const getAlertConfig = () => {
    if (!isActive) {
      return {
        severity: 'error',
        title: language === 'ar' ? '⚠️ الاشتراك منتهي' : '⚠️ Subscription Expired',
        message: language === 'ar' 
          ? 'انتهى اشتراكك. يرجى تجديد الاشتراك للاستمرار في استخدام خدمات المنصة.'
          : 'Your subscription has expired. Please renew to continue using platform services.',
        icon: <CancelIcon sx={{ fontSize: 40 }} />,
        buttonText: language === 'ar' ? 'تجديد الاشتراك' : 'Renew Subscription',
        buttonColor: '#FF3D00'
      };
    } else if (remainingDays <= 10) {
      return {
        severity: 'warning',
        title: language === 'ar' ? '⚠️ اشتراكك على وشك الانتهاء' : '⚠️ Subscription Expiring Soon',
        message: language === 'ar' 
          ? `اشتراكك سينتهي خلال ${remainingDays} يوم. يرجى تجديد الاشتراك لضمان استمرارية الخدمة.`
          : `Your subscription will expire in ${remainingDays} days. Please renew to ensure service continuity.`,
        icon: <WarningIcon sx={{ fontSize: 40 }} />,
        buttonText: language === 'ar' ? 'تجديد الآن' : 'Renew Now',
        buttonColor: '#FF9800'
      };
    } else {
      return {
        severity: 'success',
        title: language === 'ar' ? '✅ اشتراكك نشط' : '✅ Subscription Active',
        message: language === 'ar' 
          ? `اشتراكك نشط. متبقي ${remainingDays} يوم حتى انتهاء الاشتراك.`
          : `Your subscription is active. ${remainingDays} days remaining.`,
        icon: <CheckCircleIcon sx={{ fontSize: 40 }} />,
        buttonText: language === 'ar' ? 'ترقية الاشتراك' : 'Upgrade Plan',
        buttonColor: '#4CAF50'
      };
    }
  };

  const alertConfig = getAlertConfig();

  // Format date
  const formatDate = (date) => {
    if (!date) return '';
    const locale = language === 'ar' ? ar : enUS;
    return format(date, 'PPP', { locale });
  };

  // Format relative time
  const formatRelativeTime = (date) => {
    if (!date) return '';
    const locale = language === 'ar' ? ar : enUS;
    return formatDistanceToNow(date, { addSuffix: true, locale });
  };

  // Calculate discounted price
  const calculateDiscountedPrice = (originalPrice, discountPercent) => {
    return originalPrice - (originalPrice * (discountPercent / 100));
  };

  // Get current price with discount
  const getCurrentPrice = () => {
    if (!selectedPlan) return 0;
    const original = selectedPlan.priceUSD;
    if (discountPercentage > 0) {
      return calculateDiscountedPrice(original, discountPercentage);
    }
    return original;
  };

  // Validate promo code
  const validatePromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoError(language === 'ar' ? 'الرجاء إدخال كود الخصم' : 'Please enter promo code');
      return;
    }

    setValidatingPromo(true);
    setPromoError('');
    
    try {
      const response = await api.post('/promo/validate', {
        code: promoCode,
        plan_id: selectedPlan?.id,
        restaurant_id: restaurant?.id
      });
      
      if (response.data.success) {
        const discount = response.data.discount_percentage;
        const discountVal = response.data.discount_amount;
        
        setDiscountPercentage(discount);
        setDiscountAmount(discountVal);
        setPromoSuccess(true);
        setAppliedPromo({
          code: promoCode,
          discount: discount,
          message: response.data.message
        });
        
        setSnackbar({
          open: true,
          message: response.data.message || (language === 'ar' ? 'تم تطبيق الخصم بنجاح!' : 'Discount applied successfully!'),
          severity: 'success'
        });
      } else {
        setPromoError(response.data.message || (language === 'ar' ? 'كود خصم غير صالح' : 'Invalid promo code'));
        setPromoSuccess(false);
        setDiscountPercentage(0);
        setDiscountAmount(0);
        setAppliedPromo(null);
      }
    } catch (error) {
      console.error('Promo validation error:', error);
      setPromoError(error.response?.data?.message || (language === 'ar' ? 'فشل التحقق من الكود' : 'Failed to validate code'));
      setPromoSuccess(false);
      setDiscountPercentage(0);
      setDiscountAmount(0);
      setAppliedPromo(null);
    } finally {
      setValidatingPromo(false);
    }
  };

  // Remove promo code
  const removePromoCode = () => {
    setPromoCode('');
    setPromoError('');
    setPromoSuccess(false);
    setDiscountPercentage(0);
    setDiscountAmount(0);
    setAppliedPromo(null);
  };

  // Handle plan selection
  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    // Reset promo when plan changes
    if (appliedPromo) {
      removePromoCode();
    }
  };

  const handlePayment = async (plan) => {
    setSelectedPlan(plan);
    setProcessing(true);
    
    try {
      const finalAmount = getCurrentPrice();
      
      const formData = new FormData();
      formData.append('cmd', '_xclick');
      formData.append('no_note', '1');
      formData.append('lc', 'UK');
      formData.append('bn', 'PP-BuyNowBF:btn_buynow_LG.gif:NonHostedGuest');
      formData.append('userid', user?.id || '0');
      formData.append('is_mobile', 'true');
      formData.append('item_name', plan.name);
      formData.append('item_number', plan.id);
      formData.append('payment_amount', finalAmount);
      formData.append('custom', JSON.stringify({
        restaurant_id: restaurant?.id,
        plan_id: selectedPlan?.id,
        type: 'subscription',
        promo_code: appliedPromo?.code || null,
        original_amount: plan.priceUSD,
        discount_percentage: discountPercentage,
        remaining_days:remainingDays || 0
      }));
      formData.append('first_name', user?.username || '');
      
      const response = await api.post(`/paypal/stratpayment`, formData);
      const data = await response.data;
      console.log('Payment response:', data);
      
      if (data.success) {
        window.location.href = data.redirect_url;
      } else {
        setSnackbar({
          open: true,
          message: (language === 'ar' ? 'فشل في معالجة الدفع. يرجى المحاولة مرة أخرى.' : 'Payment processing failed. Please try again.'),
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Payment error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || (language === 'ar' ? 'فشل في معالجة الدفع. يرجى المحاولة مرة أخرى.' : 'Payment processing failed. Please try again.'),
        severity: 'error'
      });
    } finally {
      setProcessing(false);
    }
  };

  // Handle renew click
  const handleRenewClick = () => {
    setOpenDialog(true);
    // Reset promo when dialog opens
    removePromoCode();
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Calculate progress percentage for active subscription
  const getProgressPercentage = () => {
    if (!isActive || !expiryDate || !subscription.supscribe_update_date) return 0;
    
    const startDate = new Date(subscription.supscribe_update_date);
    const totalDays = Math.ceil((expiryDate - startDate) / (1000 * 60 * 60 * 24));
    const elapsedDays = totalDays - remainingDays;
    return (elapsedDays / totalDays) * 100;
  };

  const progressPercentage = getProgressPercentage();

  return (
    <>
      {/* Main Alert Card */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 3,
          borderRadius: '20px',
          background: `linear-gradient(135deg, 
            ${alertConfig.severity === 'error' ? 'rgba(255, 61, 0, 0.15)' : 
              alertConfig.severity === 'warning' ? 'rgba(255, 152, 0, 0.15)' : 
              'rgba(76, 175, 80, 0.15)'} 0%, 
            #0D0D0D 100%)`,
          border: `1px solid ${
            alertConfig.severity === 'error' ? '#FF3D00' : 
            alertConfig.severity === 'warning' ? '#FF9800' : 
            '#4CAF50'
          }`,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ color: alertConfig.severity === 'error' ? '#FF3D00' : 
                         alertConfig.severity === 'warning' ? '#FF9800' : '#4CAF50' }}>
            {alertConfig.icon}
          </Box>
          
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700, mb: 1 }}>
              {alertConfig.title}
            </Typography>
            <Typography variant="body2" sx={{ color: '#FFFFFF', mb: 2 }}>
              {alertConfig.message}
            </Typography>
            
            {/* Subscription Details */}
            {isActive && expiryDate && (
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={2} sx={{ mb: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarIcon sx={{ color: '#FFD700', fontSize: 16 }} />
                      <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                        {language === 'ar' ? 'ينتهي في: ' : 'Expires on: '}
                        <span style={{ color: '#FFFFFF' }}>{formatDate(expiryDate)}</span>
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccessTimeIcon sx={{ color: '#FFD700', fontSize: 16 }} />
                      <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                        {language === 'ar' ? 'متبقي: ' : 'Remaining: '}
                        <span style={{ color: '#FFFFFF', fontWeight: 600 }}>{remainingDays} {language === 'ar' ? 'يوم' : 'days'}</span>
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                
                {/* Progress Bar */}
                <LinearProgress
                  variant="determinate"
                  value={progressPercentage}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#2D2D2D',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: alertConfig.severity === 'error' ? '#FF3D00' : 
                                     alertConfig.severity === 'warning' ? '#FF9800' : '#4CAF50',
                      borderRadius: 4
                    }
                  }}
                />
              </Box>
            )}
            
            {/* Button */}
            <Button
              variant="contained"
              startIcon={<PaymentIcon />}
              onClick={handleRenewClick}
              sx={{
                backgroundColor: alertConfig.buttonColor,
                color: '#FFFFFF',
                fontWeight: 700,
                px: 3,
                py: 1,
                borderRadius: '30px',
                '&:hover': {
                  backgroundColor: alertConfig.buttonColor,
                  filter: 'brightness(0.9)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              {alertConfig.buttonText}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Subscription Plans Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#0D0D0D',
            border: '1px solid #FFD700',
            borderRadius: '24px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid #2D2D2D',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2
        }}>
          <Typography variant="h5" sx={{ color: '#FFD700', fontWeight: 800 }}>
            {language === 'ar' ? 'اختر باقة الاشتراك' : 'Choose Subscription Plan'}
          </Typography>
          <IconButton onClick={() => setOpenDialog(false)} sx={{ color: '#B3B3B3' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            {subscriptionPlans.map((plan) => (
              <Grid item xs={12} sm={6} md={3} key={plan.id}>
                <Card
                  sx={{
                    backgroundColor: '#1A1A1A',
                    borderRadius: '16px',
                    border: selectedPlan?.id === plan.id ? '2px solid #FFD700' : '1px solid #2D2D2D',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(255, 215, 0, 0.2)'
                    },
                    position: 'relative',
                    overflow: 'visible'
                  }}
                >
                  {plan.popular && (
                    <Chip
                      label={language === 'ar' ? 'الأكثر شهرة' : 'Popular'}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        right: 16,
                        backgroundColor: '#FFD700',
                        color: '#000000',
                        fontWeight: 700
                      }}
                    />
                  )}
                  {plan.bestValue && (
                    <Chip
                      label={language === 'ar' ? 'أفضل قيمة' : 'Best Value'}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        right: 16,
                        backgroundColor: '#4CAF50',
                        color: '#FFFFFF',
                        fontWeight: 700
                      }}
                    />
                  )}
                  <CardContent sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700, mb: 1 }}>
                      {language === 'ar' ? plan.name_ar : plan.name}
                    </Typography>
                    <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 800, mb: 0.5 }}>
                      {plan.priceUSD}{'$'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#B3B3B3', mb: 2, display: 'block' }}>
                      {language === 'ar' ? `لمدة ${plan.days} يوم` : `for ${plan.days} days`}
                    </Typography>
                    <Divider sx={{ my: 1.5, bgcolor: '#2D2D2D' }} />
                    <Box sx={{ textAlign: 'left', mb: 2 }}>
                      {plan.features.map((feature, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 14 }} />
                          <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    <Button
                      fullWidth
                      variant={selectedPlan?.id === plan.id ? 'contained' : 'outlined'}
                      onClick={() => handleSelectPlan(plan)}
                      sx={{
                        mt: 1,
                        borderColor: '#FFD700',
                        color: selectedPlan?.id === plan.id ? '#000000' : '#FFD700',
                        backgroundColor: selectedPlan?.id === plan.id ? '#FFD700' : 'transparent',
                        '&:hover': {
                          backgroundColor: selectedPlan?.id === plan.id ? '#FFE55C' : 'rgba(255, 215, 0, 0.1)'
                        }
                      }}
                    >
                      {language === 'ar' ? 'اختر' : 'Select'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Promo Code Section */}
          {selectedPlan && (
            <Box sx={{ mt: 3, p: 2, bgcolor: '#1A1A1A', borderRadius: '16px' }}>
              <Typography variant="subtitle1" sx={{ color: '#FFD700', fontWeight: 600, mb: 2 }}>
                <LocalOfferIcon sx={{ mr: 1, fontSize: 20, verticalAlign: 'middle' }} />
                {language === 'ar' ? 'كود الخصم' : 'Promo Code'}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder={language === 'ar' ? 'أدخل كود الخصم' : 'Enter promo code'}
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value);
                    setPromoError('');
                  }}
                  disabled={promoSuccess}
                  error={!!promoError}
                  helperText={promoError}
                  sx={{
                    flex: 2,
                    minWidth: '200px',
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#0D0D0D',
                      '& fieldset': { borderColor: '#2D2D2D' },
                      '&:hover fieldset': { borderColor: '#FFD700' },
                    },
                    '& .MuiInputLabel-root': { color: '#B3B3B3' },
                    '& .MuiOutlinedInput-input': { color: '#FFFFFF' }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DiscountIcon sx={{ color: '#FFD700', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
                {!promoSuccess ? (
                  <Button
                    variant="outlined"
                    onClick={validatePromoCode}
                    disabled={validatingPromo || !promoCode}
                    sx={{
                      borderColor: '#FFD700',
                      color: '#FFD700',
                      '&:hover': {
                        borderColor: '#FFE55C',
                        backgroundColor: 'rgba(255, 215, 0, 0.1)'
                      }
                    }}
                  >
                    {validatingPromo ? <CircularProgress size={20} /> : (language === 'ar' ? 'تطبيق' : 'Apply')}
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    onClick={removePromoCode}
                    sx={{
                      borderColor: '#F44336',
                      color: '#F44336',
                      '&:hover': {
                        borderColor: '#D32F2F',
                        backgroundColor: 'rgba(244, 67, 54, 0.1)'
                      }
                    }}
                  >
                    {language === 'ar' ? 'إزالة' : 'Remove'}
                  </Button>
                )}
              </Box>
              
              {/* Applied Promo Message */}
              {appliedPromo && (
                <Alert 
                  severity="success" 
                  sx={{ 
                    mt: 2,
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    color: '#4CAF50',
                    border: '1px solid #4CAF50',
                    borderRadius: '8px'
                  }}
                >
                  <Typography variant="body2">
                    {language === 'ar' 
                      ? `تم تطبيق الخصم بنجاح! خصم ${appliedPromo.discount}% على الباقة`
                      : `Discount applied successfully! ${appliedPromo.discount}% off on this plan`}
                  </Typography>
                </Alert>
              )}
            </Box>
          )}

          {/* Payment Summary */}
          {selectedPlan && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#1A1A1A', borderRadius: '16px' }}>
              <Typography variant="subtitle1" sx={{ color: '#FFD700', fontWeight: 600, mb: 1 }}>
                {language === 'ar' ? 'ملخص الدفع' : 'Payment Summary'}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                    {language === 'ar' ? 'الباقة' : 'Plan'}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                    {language === 'ar' ? selectedPlan.name_ar : selectedPlan.name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                    {language === 'ar' ? 'السعر الأصلي' : 'Original Price'}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#B3B3B3', textDecoration: discountPercentage > 0 ? 'line-through' : 'none' }}>
                    {selectedPlan.priceUSD}{'$'}
                  </Typography>
                </Grid>
                {discountPercentage > 0 && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: '#4CAF50' }}>
                        {language === 'ar' ? 'الخصم' : 'Discount'}
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#4CAF50', fontWeight: 600 }}>
                        -{discountPercentage}% ({discountAmount || (selectedPlan.priceUSD * discountPercentage / 100).toFixed(2)}$)
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: '#FFD700' }}>
                        {language === 'ar' ? 'المبلغ النهائي' : 'Final Amount'}
                      </Typography>
                      <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700 }}>
                        {getCurrentPrice().toFixed(2)}$
                      </Typography>
                    </Grid>
                  </>
                )}
                {!discountPercentage && (
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ color: '#FFD700' }}>
                      {language === 'ar' ? 'المبلغ' : 'Amount'}
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700 }}>
                      {selectedPlan.priceUSD}{'$'}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
          {/* Payment Info / Contact Section */}
<Box
  sx={{
    mt: 2,
    p: 2,
    bgcolor: 'rgba(255, 215, 0, 0.05)',
    border: '1px dashed #FFD700',
    borderRadius: '16px',
    textAlign: 'center',
  }}
>
  <Typography
    variant="body2"
    sx={{ color: '#B3B3B3', mb: 1.5, lineHeight: 1.7 }}
  >
    {language === 'ar'
      ? 'إذا لم تتمكن من الدفع باستخدام باي بال يرجى التواصل معنا على واتساب للحصول على معلومات الدفع.'
      : "If you can't pay using PayPal, please contact us on WhatsApp for payment info."}
  </Typography>

  <Button
    variant="outlined"
    startIcon={<WhatsAppIcon />}
    href="https://wa.me/963936179303"
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      borderColor: '#25D366',
      color: '#25D366',
      fontWeight: 700,
      borderRadius: '50px',
      px: 3,
      textTransform: 'none',
      '&:hover': {
        borderColor: '#1EBE57',
        backgroundColor: 'rgba(37, 211, 102, 0.1)',
      },
    }}
  >
    {language === 'ar' ? '+963936179303' : '+963 936 179 303'}
  </Button>
</Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, borderTop: '1px solid #2D2D2D' }}>
          <Button
            onClick={() => setOpenDialog(false)}
            sx={{ color: '#B3B3B3' }}
          >
            {language === 'ar' ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button
            variant="contained"
            disabled={!selectedPlan || processing}
            onClick={() => handlePayment(selectedPlan)}
            startIcon={processing ? <CircularProgress size={20} /> : <CreditCardIcon />}
            sx={{
              backgroundColor: '#FFD700',
              color: '#000000',
              fontWeight: 700,
              '&:hover': { backgroundColor: '#FFE55C' }
            }}
          >
            {processing 
              ? (language === 'ar' ? 'جاري المعالجة...' : 'Processing...')
              : (language === 'ar' ? 'الدفع الآن' : 'Pay Now')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            backgroundColor: '#0D0D0D',
            color: '#FFFFFF',
            border: `1px solid ${snackbar.severity === 'success' ? '#4CAF50' : '#FF3D00'}`,
            borderRadius: '12px'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SubscriptionAlert;